import { Box } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    copyToClipboard,
    CustomNoRowsOverlay,
    CustomDataGridSkeleton,
    fireErrorToast,
} from '../../../utils/commonFunction';
import {
    defaultPageSize,
    PENDING_DISPUTE,
    RESOLVE_DISPUTE,
} from '../../../constant/constant';
import { useAccount } from 'wagmi';
import Loader from '../../../components/loader/Loader';
import axios from 'axios';
import BackendWalletSignInPanel from '../../../components/auth/BackendWalletSignInPanel';
import {
    API_BASE_URL,
    ensureAdminBackendSession,
    getStoredBackendToken,
} from '../../../api/backendClient';
import { useBackendWalletAuth } from '../../../hooks/useBackendWalletAuth';
import { useTheme } from '../../../context/ThemeContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    DISPUTE_CATEGORY_JOB,
    DISPUTE_CATEGORY_PAYMENT,
    JOB_DISPUTE_ACCENT,
} from './adminDisputeUtils';

const partitionDisputes = (rows = []) => {
    const resolvedStatuses = new Set(['resolved', 'closed']);
    const pending = [];
    const resolved = [];
    rows.forEach((dispute) => {
        if (resolvedStatuses.has(String(dispute.status || '').toLowerCase())) {
            resolved.push(dispute);
        } else {
            pending.push(dispute);
        }
    });
    return { pending, resolved };
};

const ADMIN_DISPUTE_GRID_SX_LIGHT = {
    width: '100%',
    height: 500,
    '& .MuiDataGrid-root': {
        border: 'none',
        color: '#3f3f46',
        '--DataGrid-rowBorderColor': '#e4e4e7',
    },
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: '#f4f4f5',
        color: '#71717a',
        fontSize: '0.7rem',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontWeight: 700,
        borderBottom: '1px solid #e4e4e7',
        borderRadius: 0,
    },
    '& .MuiDataGrid-cell': {
        fontSize: '0.8125rem',
        borderBottom: '1px solid #e4e4e7',
        display: 'flex',
        alignItems: 'center',
        paddingLeft: '14px',
        paddingRight: '14px',
    },
    '& .MuiDataGrid-row:nth-of-type(even)': {
        backgroundColor: '#fafafa',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
        backgroundColor: '#ffffff',
    },
    '& .MuiDataGrid-row:hover': {
        backgroundColor: '#f4f4f5',
    },
    '& .MuiDataGrid-footerContainer': {
        borderTop: '1px solid #e4e4e7',
        backgroundColor: '#fafafa',
    },
    '& .MuiTablePagination-root': {
        color: '#3f3f46',
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        color: '#52525b',
        fontSize: '0.78rem',
    },
    '& .MuiTablePagination-actions button': {
        color: '#3f3f46',
    },
    '& .MuiTablePagination-actions button.Mui-disabled': {
        color: 'rgba(63, 63, 70, 0.35)',
    },
    '& .MuiSvgIcon-root': {
        color: '#3f3f46',
    },
    '& .MuiDataGrid-virtualScroller': {
        background: '#ffffff',
        scrollbarWidth: 'thin',
        scrollbarColor: '#d4d4d8 #fafafa',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
        backgroundColor: '#d4d4d8',
        borderRadius: '6px',
        border: '2px solid #fafafa',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover': {
        backgroundColor: '#a1a1aa',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
        backgroundColor: '#fafafa',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 700,
    },
};

const ADMIN_DISPUTE_GRID_SX_DARK = {
    width: '100%',
    height: 500,
    '& .MuiDataGrid-root': {
        border: 'none',
        color: '#d4d4d8',
        '--DataGrid-rowBorderColor': '#3f3f46',
    },
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: '#18181b',
        color: '#a1a1aa',
        fontSize: '0.7rem',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontWeight: 700,
        borderBottom: '1px solid #3f3f46',
        borderRadius: 0,
    },
    '& .MuiDataGrid-cell': {
        fontSize: '0.8125rem',
        borderBottom: '1px solid #3f3f46',
        display: 'flex',
        alignItems: 'center',
        paddingLeft: '14px',
        paddingRight: '14px',
    },
    '& .MuiDataGrid-row:nth-of-type(even)': {
        backgroundColor: '#0c0c0e',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
        backgroundColor: '#09090b',
    },
    '& .MuiDataGrid-row:hover': {
        backgroundColor: '#18181b',
    },
    '& .MuiDataGrid-footerContainer': {
        borderTop: '1px solid #3f3f46',
        backgroundColor: '#18181b',
    },
    '& .MuiTablePagination-root': {
        color: '#e4e4e7',
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        color: '#a1a1aa',
        fontSize: '0.78rem',
    },
    '& .MuiTablePagination-actions button': {
        color: '#e4e4e7',
    },
    '& .MuiTablePagination-actions button.Mui-disabled': {
        color: 'rgba(161, 161, 170, 0.4)',
    },
    '& .MuiSvgIcon-root': {
        color: '#e4e4e7',
    },
    '& .MuiDataGrid-virtualScroller': {
        background: '#09090b',
        scrollbarWidth: 'thin',
        scrollbarColor: '#3f3f46 #18181b',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
        backgroundColor: '#3f3f46',
        borderRadius: '6px',
        border: '2px solid #18181b',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover': {
        backgroundColor: '#52525b',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
        backgroundColor: '#18181b',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 700,
    },
};

const formatTableDate = (value) => {
    if (value == null || value === '') return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toISOString().slice(0, 10);
};

const formatDisputeIdCell = (raw) => {
    if (raw == null || raw === '') return '—';
    const s = String(raw).trim();
    if (/^dsp-/i.test(s)) return s;
    return `DSP-${s}`;
};

const formatPaymentIdCell = (raw) => {
    if (raw == null || raw === '') return '—';
    const s = String(raw).trim().replace(/^#/, '');
    if (/^pay-/i.test(s)) return s.toUpperCase();
    return `PAY-${s}`;
};

const formatTaskIdCell = (raw) => {
    if (raw == null || raw === '') return '—';
    const s = String(raw).trim();
    if (s.length <= 12) return s;
    return `${s.slice(0, 8)}…`;
};

const shortenWallet = (raw) => {
    if (raw == null || raw === '') return '—';
    const s = String(raw).trim();
    if (s.length <= 12) return s;
    return `${s.slice(0, 6)}…${s.slice(-4)}`;
};

const formatAmountCell = (row) => {
    const v = row.amount;
    if (v == null || v === '') return '—';
    const s = String(v).trim();
    if (s === '—') return '—';
    return s;
};

const formatDueCell = (row) => {
    const v = row.dueDate ?? row.due ?? row.deadline;
    if (v == null || v === '' || v === '—') return '—';
    if (typeof v === 'number' && v > 1e12) return formatTableDate(v);
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    return String(v);
};

const statusDisplayLabel = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'pending') return 'Pending review';
    if (s === 'in_review') return 'Under review';
    if (s === 'resolved') return 'Resolved';
    if (s === 'closed') return 'Closed';
    return status || '—';
};

const ReviewEyeIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

/** Shared shape for outlined pills */
const OUTLINED_PILL_SHAPE =
    'inline-flex max-w-full items-center rounded-lg border px-3 py-1.5 text-xs font-medium';

/** Neutral Review button (unchanged look) */
const OUTLINED_CONTROL_SHELL = `${OUTLINED_PILL_SHAPE} border-zinc-300 bg-transparent text-zinc-700 dark:border-zinc-600 dark:text-zinc-300`;

const REVIEW_BUTTON_CLASS = `${OUTLINED_CONTROL_SHELL} cursor-pointer gap-1.5 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-zinc-400`;

const statusOutlinedTone = (statusKey) => {
    const key = String(statusKey || '').toLowerCase();
    if (key === 'pending') {
        return 'border-amber-800/20 bg-amber-50/90 text-amber-950 dark:border-amber-700/35 dark:bg-amber-950/35 dark:text-amber-100/90';
    }
    if (key === 'in_review') {
        return 'border-sky-800/20 bg-sky-50/85 text-sky-950 dark:border-sky-700/30 dark:bg-sky-950/30 dark:text-sky-100/90';
    }
    if (key === 'resolved' || key === 'closed') {
        return 'border-emerald-800/20 bg-emerald-50/90 text-emerald-950 dark:border-emerald-700/30 dark:bg-emerald-950/30 dark:text-emerald-100/90';
    }
    return 'border-zinc-300 bg-zinc-50/90 text-zinc-800 dark:border-zinc-600 dark:bg-zinc-900/45 dark:text-zinc-200';
};

const AdminDispute = () => {
    const [searchInput, setSearchInput] = useState('');
    const [disputeCategory, setDisputeCategory] = useState(
        DISPUTE_CATEGORY_PAYMENT,
    );
    const [activeSection, setActiveSection] = useState(PENDING_DISPUTE);
    const [paymentPending, setPaymentPending] = useState([]);
    const [paymentResolved, setPaymentResolved] = useState([]);
    const [jobPending, setJobPending] = useState([]);
    const [jobResolved, setJobResolved] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const setCategory = (category) => {
        setDisputeCategory(category);
        setSearchParams({ category });
    };

    const { address: walletAddress } = useAccount();
    const { resolvedTheme } = useTheme();
    const {
        status: authStatus,
        error: authError,
        signIn,
        isReady: isBackendReady,
    } = useBackendWalletAuth({
        walletAddress,
        rolePreference: 'employee',
        enabled: Boolean(walletAddress),
    });

    useEffect(() => {
        const category = searchParams.get('category');
        if (category === DISPUTE_CATEGORY_JOB) {
            setDisputeCategory(DISPUTE_CATEGORY_JOB);
        } else if (category === DISPUTE_CATEGORY_PAYMENT) {
            setDisputeCategory(DISPUTE_CATEGORY_PAYMENT);
        }
    }, [searchParams]);

    const handleSearchChange = (event) => {
        setSearchInput(event.target.value);
    };

    const handleReviewCase = (dispute) => {
        if (!dispute?.id) return;
        navigate(`/admin-dispute/${dispute.id}`, {
            state: { dispute, category: disputeCategory },
        });
    };

    const colDisputeId = {
        field: 'disputeId',
        headerName: 'Dispute ID',
        minWidth: 120,
        flex: 0.55,
        renderCell: (p) => (
            <span className="font-mono text-xs font-medium text-stone-700 dark:text-stone-400">
                {formatDisputeIdCell(p.value)}
            </span>
        ),
    };

    const colPaymentId = {
        field: 'paymentId',
        headerName: 'Payment ID',
        minWidth: 120,
        flex: 0.55,
        renderCell: (p) => (
            <span className="font-mono text-xs font-medium text-slate-600 dark:text-slate-400">
                {formatPaymentIdCell(p.value)}
            </span>
        ),
    };

    const colAmount = {
        field: 'amount',
        headerName: 'Amount',
        minWidth: 110,
        flex: 0.45,
        sortable: false,
        renderCell: (p) => (
            <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                {formatAmountCell(p.row)}
            </span>
        ),
    };

    const colRaised = {
        field: 'createdAt',
        headerName: 'Raised',
        minWidth: 110,
        flex: 0.4,
        renderCell: (p) => (
            <span className="text-xs text-zinc-500 dark:text-zinc-500">
                {formatTableDate(p.value)}
            </span>
        ),
    };

    const colDue = {
        field: 'dueDisplay',
        headerName: 'Due',
        minWidth: 110,
        flex: 0.4,
        sortable: false,
        valueGetter: (_value, row) => formatDueCell(row),
        renderCell: (p) => (
            <span className="text-xs text-zinc-500 dark:text-zinc-500">{p.value}</span>
        ),
    };

    const colUpdated = {
        field: 'updatedAt',
        headerName: 'Updated',
        minWidth: 110,
        flex: 0.4,
        renderCell: (p) => (
            <span className="text-xs text-zinc-500 dark:text-zinc-500">
                {formatTableDate(p.value)}
            </span>
        ),
    };

    const statusColumn = {
        field: 'status',
        headerName: 'Status',
        minWidth: 150,
        flex: 0.5,
        renderCell: (params) => {
            const key = String(params.value || '').toLowerCase();
            const label = statusDisplayLabel(params.value);
            return (
                <span
                    className={`${OUTLINED_PILL_SHAPE} ${statusOutlinedTone(key)}`}
                    title={label}
                >
                    {label}
                </span>
            );
        },
    };

    const reviewActionColumn = {
        field: 'action',
        headerName: 'Action',
        minWidth: 130,
        flex: 0.45,
        align: 'right',
        headerAlign: 'right',
        sortable: false,
        filterable: false,
        renderCell: (params) => (
            <button
                type="button"
                className={REVIEW_BUTTON_CLASS}
                onClick={() => handleReviewCase(params.row)}
            >
                <span className="shrink-0 text-zinc-500 dark:text-zinc-400" aria-hidden>
                    <ReviewEyeIcon />
                </span>
                Review
            </button>
        ),
    };

    const allPendingColumns = [
        colDisputeId,
        colPaymentId,
        colAmount,
        colRaised,
        colDue,
        statusColumn,
        reviewActionColumn,
    ];

    const allResolveColumns = [
        colDisputeId,
        colPaymentId,
        colAmount,
        colRaised,
        colUpdated,
        statusColumn,
        reviewActionColumn,
    ];

    const colTaskId = {
        field: 'taskId',
        headerName: 'Task ID',
        minWidth: 120,
        flex: 0.5,
        renderCell: (p) => (
            <span
                className={`font-mono text-xs font-medium ${JOB_DISPUTE_ACCENT.monoText}`}
            >
                {formatTaskIdCell(p.value)}
            </span>
        ),
    };

    const colTaskTitle = {
        field: 'taskTitle',
        headerName: 'Task',
        minWidth: 160,
        flex: 0.7,
        sortable: false,
        renderCell: (p) => (
            <span
                className="line-clamp-2 text-sm text-zinc-800 dark:text-zinc-200"
                title={p.value || p.row?.description || ''}
            >
                {p.value ||
                    (p.row?.description
                        ? String(p.row.description).slice(0, 60)
                        : '—')}
            </span>
        ),
    };

    const colRaisedBy = {
        field: 'raisedByWallet',
        headerName: 'Raised by',
        minWidth: 130,
        flex: 0.55,
        renderCell: (p) => (
            <span
                className="font-mono text-xs text-zinc-600 dark:text-zinc-400"
                title={p.value || ''}
            >
                {shortenWallet(p.value)}
            </span>
        ),
    };

    const colEscrowId = {
        field: 'taskEscrowId',
        headerName: 'Escrow',
        minWidth: 90,
        flex: 0.35,
        renderCell: (p) => (
            <span className="font-mono text-xs text-zinc-600 dark:text-zinc-400">
                {p.value != null && p.value !== '' ? `#${p.value}` : '—'}
            </span>
        ),
    };

    const jobPendingColumns = [
        colTaskId,
        colTaskTitle,
        colEscrowId,
        colRaisedBy,
        colRaised,
        statusColumn,
        reviewActionColumn,
    ];

    const jobResolveColumns = [
        colTaskId,
        colTaskTitle,
        colEscrowId,
        colRaisedBy,
        colRaised,
        colUpdated,
        statusColumn,
        reviewActionColumn,
    ];

    const gridColumns =
        disputeCategory === DISPUTE_CATEGORY_PAYMENT
            ? activeSection === PENDING_DISPUTE
                ? allPendingColumns
                : allResolveColumns
            : activeSection === PENDING_DISPUTE
              ? jobPendingColumns
              : jobResolveColumns;

    const handlePaginationModelChange = (model) => {
        setPageSize(model.pageSize);
    };

    const loadDisputes = useCallback(async () => {
        if (!walletAddress || !isBackendReady) return;
        try {
            setDataLoading(true);
            const token =
                getStoredBackendToken(walletAddress) ||
                (await ensureAdminBackendSession(walletAddress));
            if (!token) {
                fireErrorToast(
                    'Could not sign in to the admin dispute API. Approve the wallet signature request, then try again.',
                );
                return;
            }
            const headers = { Authorization: `Bearer ${token}` };
            const [paymentRes, jobRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/disputes?type=payment`, {
                    headers,
                }),
                axios.get(`${API_BASE_URL}/disputes?type=task`, { headers }),
            ]);

            const paymentRows = paymentRes?.data?.data || [];
            const jobRows = jobRes?.data?.data || [];
            const paymentParts = partitionDisputes(paymentRows);
            const jobParts = partitionDisputes(jobRows);

            setPaymentPending(paymentParts.pending);
            setPaymentResolved(paymentParts.resolved);
            setJobPending(jobParts.pending);
            setJobResolved(jobParts.resolved);
        } catch (error) {
            console.log('Error getting data :', error);
            const msg =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to load disputes from backend';
            fireErrorToast(msg);
        } finally {
            setDataLoading(false);
        }
    }, [walletAddress, isBackendReady]);

    useEffect(() => {
        loadDisputes();
    }, [loadDisputes]);

    const dataGridSx = useMemo(
        () =>
            resolvedTheme === 'dark'
                ? ADMIN_DISPUTE_GRID_SX_DARK
                : ADMIN_DISPUTE_GRID_SX_LIGHT,
        [resolvedTheme],
    );

    const categoryPending =
        disputeCategory === DISPUTE_CATEGORY_PAYMENT
            ? paymentPending
            : jobPending;
    const categoryResolved =
        disputeCategory === DISPUTE_CATEGORY_PAYMENT
            ? paymentResolved
            : jobResolved;

    const matchesDisputeSearch = (row, q) => {
        if (!q) return true;
        const haystack = [
            row.description,
            row.reason,
            row.paymentId,
            row.disputeId,
            row.taskId,
            row.taskTitle,
            row.raisedByWallet,
            row.taskEscrowId,
            formatDisputeIdCell(row.disputeId),
            formatPaymentIdCell(row.paymentId),
            formatTaskIdCell(row.taskId),
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
        return haystack.includes(q);
    };

    const filteredPendingRows = useMemo(() => {
        const q = searchInput.trim().toLowerCase();
        if (!q) return categoryPending;
        return categoryPending.filter((row) => matchesDisputeSearch(row, q));
    }, [categoryPending, searchInput]);

    const filteredResolvedRows = useMemo(() => {
        const q = searchInput.trim().toLowerCase();
        if (!q) return categoryResolved;
        return categoryResolved.filter((row) => matchesDisputeSearch(row, q));
    }, [categoryResolved, searchInput]);

    return (
        <div className="crypto-page relative min-h-[calc(100vh-80px)] overflow-hidden">
            <div className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                {walletAddress && !isBackendReady ? (
                    <BackendWalletSignInPanel
                        walletAddress={walletAddress}
                        status={authStatus}
                        error={authError}
                        onRetry={signIn}
                        title="Sign in for admin disputes"
                        description="Sign a free message to verify your admin wallet and load the dispute queue from the server."
                    />
                ) : (
                    <>
                {/* ADMIN HEADER */}
                <div className="mb-10 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
                    <div className="animate-fade-in-up">
                        <p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-teal-700 dark:text-teal-400">
                            Admin Control
                        </p>
                        <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 dark:text-white md:text-5xl">
                            Dispute queue
                        </h1>
                        <p className="mt-3 max-w-xl text-lg text-zinc-600 dark:text-zinc-400">
                            {disputeCategory === DISPUTE_CATEGORY_PAYMENT
                                ? 'CronPay payment disputes. Resolve on the main CPG contract.'
                                : 'Jobs and escrow task disputes. Resolve in the Jobs portal on the task escrow contract.'}
                        </p>
                    </div>

                    <div className="group relative w-full animate-fade-in-up md:w-80">
                        <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-4">
                            <svg
                                className="h-4 w-4 text-zinc-400 transition-colors group-focus-within:text-zinc-600 dark:group-focus-within:text-zinc-300"
                                fill="none"
                                viewBox="0 0 20 20"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                                />
                            </svg>
                        </div>
                        <input
                            type="search"
                            className="cpg-input block w-full rounded-2xl py-3.5 ps-11 text-sm"
                            placeholder="Search disputes..."
                            value={searchInput}
                            onChange={handleSearchChange}
                        />
                    </div>
                </div>

                {/* PAYMENT vs JOB */}
                <div className="mb-8 flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={() => setCategory(DISPUTE_CATEGORY_PAYMENT)}
                        className={`rounded-2xl border px-5 py-3 text-left transition-all ${
                            disputeCategory === DISPUTE_CATEGORY_PAYMENT
                                ? 'border-teal-400 bg-teal-50 shadow-sm ring-1 ring-teal-200 dark:border-teal-700 dark:bg-teal-950/40 dark:ring-teal-900/50'
                                : 'border-zinc-200 bg-white hover:border-zinc-300 dark:border-zinc-800 dark:bg-zinc-900/40'
                        }`}
                    >
                        <span className="flex items-center gap-2 text-sm font-semibold text-zinc-900 dark:text-white">
                            <i className="fa-solid fa-coins text-teal-600 dark:text-teal-400" />
                            Payment disputes
                        </span>
                        <span className="mt-1 block text-xs text-zinc-500 dark:text-zinc-400">
                            Tracked / locked CronPay payments
                        </span>
                        <span className="mt-2 inline-flex rounded-full bg-teal-600/10 px-2.5 py-0.5 text-xs font-bold text-teal-800 dark:text-teal-300">
                            {paymentPending.length} open · {paymentResolved.length}{' '}
                            closed
                        </span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setCategory(DISPUTE_CATEGORY_JOB)}
                        className={`rounded-2xl border px-5 py-3 text-left transition-all ${
                            disputeCategory === DISPUTE_CATEGORY_JOB
                                ? JOB_DISPUTE_ACCENT.activeCard
                                : 'border-zinc-200 bg-white hover:border-zinc-300 dark:border-zinc-800 dark:bg-zinc-900/40'
                        }`}
                    >
                        <span className="flex items-center gap-2 text-sm font-semibold text-zinc-900 dark:text-white">
                            <i
                                className={`fa-solid fa-briefcase ${JOB_DISPUTE_ACCENT.icon}`}
                            />
                            Job / escrow disputes
                        </span>
                        <span className="mt-1 block text-xs text-zinc-500 dark:text-zinc-400">
                            Company tasks funded on task escrow
                        </span>
                        <span
                            className={`mt-2 inline-flex rounded-full px-2.5 py-0.5 text-xs font-bold ${JOB_DISPUTE_ACCENT.countPill}`}
                        >
                            {jobPending.length} open · {jobResolved.length} closed
                        </span>
                    </button>
                </div>

                {/* SECTION SWITCHER */}
                <div className="mb-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div
                        onClick={() => setActiveSection(PENDING_DISPUTE)}
                        className={`group animate-fade-in-up cursor-pointer rounded-2xl border p-6 transition-all ${activeSection === PENDING_DISPUTE ? 'border-teal-300 bg-teal-50 shadow-sm ring-1 ring-teal-200 dark:border-teal-800 dark:bg-teal-950/40 dark:ring-teal-900/50' : 'border-zinc-200 bg-white hover:border-zinc-300 dark:border-zinc-800 dark:bg-zinc-900/40 dark:hover:border-zinc-700'}`}
                    >
                        <div className="flex items-start justify-between">
                            <p
                                className={`text-xs font-bold uppercase tracking-widest ${activeSection === PENDING_DISPUTE ? 'text-teal-800 dark:text-teal-300' : 'text-zinc-500 dark:text-zinc-500'}`}
                            >
                                Pending Action
                            </p>
                            <div
                                className={`rounded-lg p-2 ${activeSection === PENDING_DISPUTE ? 'bg-teal-600 text-white dark:bg-teal-500' : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-500'}`}
                            >
                                <i className="fas fa-clock text-sm"></i>
                            </div>
                        </div>
                        <div className="mt-2 text-3xl font-bold text-zinc-900 dark:text-white">
                            {dataLoading ? (
                                <Loader size="20px" />
                            ) : (
                                categoryPending.length
                            )}
                        </div>
                    </div>

                    <div
                        onClick={() => setActiveSection(RESOLVE_DISPUTE)}
                        className={`group animate-fade-in-up cursor-pointer rounded-2xl border p-6 transition-all ${activeSection === RESOLVE_DISPUTE ? 'border-emerald-300 bg-emerald-50 shadow-sm ring-1 ring-emerald-200 dark:border-emerald-800 dark:bg-emerald-950/30 dark:ring-emerald-900/40' : 'border-zinc-200 bg-white hover:border-zinc-300 dark:border-zinc-800 dark:bg-zinc-900/40 dark:hover:border-zinc-700'}`}
                    >
                        <div className="flex items-start justify-between">
                            <p
                                className={`text-xs font-bold uppercase tracking-widest ${activeSection === RESOLVE_DISPUTE ? 'text-emerald-800 dark:text-emerald-300' : 'text-zinc-500 dark:text-zinc-500'}`}
                            >
                                History
                            </p>
                            <div
                                className={`rounded-lg p-2 ${activeSection === RESOLVE_DISPUTE ? 'bg-emerald-600 text-white dark:bg-emerald-500' : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-500'}`}
                            >
                                <i className="fas fa-history text-sm"></i>
                            </div>
                        </div>
                        <div className="mt-2 text-3xl font-bold text-zinc-900 dark:text-white">
                            {dataLoading ? (
                                <Loader size="20px" />
                            ) : (
                                categoryResolved.length
                            )}
                        </div>
                    </div>
                </div>

                {/* TABLE SECTION */}
                <div className="glass-card animate-fade-in-up overflow-hidden rounded-3xl border border-zinc-200 shadow-xl dark:border-zinc-800">
                    <Box sx={dataGridSx}>
                        <DataGrid
                            rows={
                                activeSection === PENDING_DISPUTE
                                    ? filteredPendingRows
                                    : filteredResolvedRows
                            }
                            columns={gridColumns}
                            getRowId={(row) => row.id}
                            loading={dataLoading}
                            rowHeight={75}
                            initialState={{
                                pagination: { paginationModel: { pageSize } },
                            }}
                            onPaginationModelChange={
                                handlePaginationModelChange
                            }
                            slots={{
                                noRowsOverlay: CustomNoRowsOverlay,
                                loadingOverlay: CustomDataGridSkeleton,
                            }}
                        />
                    </Box>
                </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminDispute;
